import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { useExportFile, useProject } from '@/lib/api';

type ExportFormat = 'po' | 'pot' | 'json';

export function ProjectExportTab() {
  const { projId = '' } = useParams<{ projId: string }>();
  const project = useProject(projId);
  const exportFile = useExportFile();
  const toast = useToast();
  const [format, setFormat] = useState<ExportFormat>('po');
  const [busy, setBusy] = useState<string | null>(null);

  if (project.isLoading) return <Spinner label="Loading…" />;
  if (!project.data) return null;

  const languages: string[] = [
    project.data.sourceLanguageCode,
    ...(project.data.targetLanguageCodes ?? []),
  ];

  async function download(lang: string) {
    setBusy(lang);
    try {
      const name = await exportFile.mutateAsync({
        projectID: projId,
        lang,
        format,
        suggestedName: `${project.data!.slug}-${lang}.${format}`,
      });
      toast.show(`Downloaded ${name}`, 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <FileDown className="size-4 text-primary" aria-hidden />
          Per-language export
        </CardTitle>
        <CardDescription>
          One file per language. Format defaults to the project's default; override below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="export-fmt">Format</Label>
            <select
              id="export-fmt"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              className="flex h-9 w-32 rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="po">PO</option>
              <option value="pot">POT</option>
              <option value="json">JSON</option>
            </select>
          </div>
        </div>

        {languages.length === 0 ? (
          <EmptyState
            icon={Download}
            title="No languages on this project"
            description="Add target languages in Settings before exporting."
          />
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {languages.map((lang) => {
              const isSource = lang === project.data?.sourceLanguageCode;
              return (
                <li
                  key={lang}
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
                >
                  <div className="inline-flex items-center gap-2">
                    <Badge tone={isSource ? 'primary' : 'outline'}>{lang}</Badge>
                    {isSource && <span className="text-xs text-muted-foreground">source</span>}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void download(lang)}
                    disabled={busy === lang}
                  >
                    <Download aria-hidden />
                    {busy === lang ? 'Downloading…' : `${format.toUpperCase()}`}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
